import {
  InfiniteData,
  queryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import queryString from 'query-string'
import { useCallback } from 'react'
import {
  AutomationRuleCondition,
  AutomationRuleDetailModel,
  AutomationRuleDetailResponse,
  AutomationRuleFundingSource,
  AutomationRuleListModel,
  AutomationRuleListResponse,
  AutomationRuleStatusType,
} from 'src/models/automationRules.models'
import { PaginationResponse } from 'src/models/pagination.models'
import { getAutomationRuleDetail } from 'src/queries/organizations/automationRules.network'
import { automationRulesQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getNextExtendPageParam,
  MutationMethod,
  useExtendInfiniteQuery,
  useExtendMutation,
  UseExtendMutationVariablesBody,
} from 'src/queries/queries.utils'

export type PostAutomationRuleRequest = {
  name: string
  conditions: AutomationRuleCondition[]
  mappings: Array<{
    categoryId: string
    labelId: string
  }>
}

export type PutAutomationRuleRequest = PostAutomationRuleRequest & {
  status: AutomationRuleStatusType
}

// TODO: backend doesn't allow patching of conditions
export type PatchAutomationRuleRequest = Omit<
  Partial<PutAutomationRuleRequest>,
  'conditions'
>

export const usePostAutomationRule = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    AutomationRuleCondition,
    UseExtendMutationVariablesBody<PostAutomationRuleRequest>
  >(MutationMethod.POST, {
    mutationUrl: () => `/rules`,
    onSuccess: () => {
      // update list responses
      queryClient.invalidateQueries({
        queryKey: automationRulesQueryKeys.all(),
      })
    },
  })
}

export interface PutAutomationRuleVariables {
  id: string
  body: PutAutomationRuleRequest
}

export const usePutAutomationRule = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    AutomationRuleListResponse,
    PutAutomationRuleVariables
  >(MutationMethod.PUT, {
    mutationUrl: ({ id }) => `/rules/${id}`,
    onSuccess: () => {
      // update list responses
      queryClient.invalidateQueries({
        queryKey: automationRulesQueryKeys.all(),
      })
    },
  })
}

export interface PatchAutomationRuleVariables {
  id: string
  body: PatchAutomationRuleRequest
}

export const usePatchAutomationRule = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    AutomationRuleListResponse,
    PatchAutomationRuleVariables
  >(MutationMethod.PATCH, {
    mutationUrl: ({ id }) => `/rules/${id}`,
    mutationKey: automationRulesQueryKeys.patch(),
    onSuccess: () => {
      // update list responses
      queryClient.invalidateQueries({
        queryKey: automationRulesQueryKeys.all(),
      })
    },
  })
}

interface QueryAutomationRuleDetailOptions
  extends ExtendBaseQueryOptions<
    AutomationRuleDetailResponse,
    AutomationRuleDetailModel
  > {
  ruleId: string | undefined
}

export const automationRuleQueries = {
  detail: (ruleId: string | undefined) =>
    queryOptions({
      queryKey: automationRulesQueryKeys.detail(ruleId),
      queryFn: () => {
        // enabled gates the initial fetch, but refetch()/enabled overrides
        // can still reach queryFn without a ruleId — guard instead of
        // asserting so we fail loudly rather than request `/rules/undefined`.
        if (!ruleId) {
          throw new Error('automationRuleQueries.detail: ruleId is required')
        }
        return getAutomationRuleDetail(ruleId)
      },
      enabled: ruleId !== undefined,
    }),
}

const selectAutomationRuleDetailModel = (
  data: AutomationRuleDetailResponse,
): AutomationRuleDetailModel => new AutomationRuleDetailModel(data)

export const useAutomationRuleDetail = ({
  ruleId,
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryAutomationRuleDetailOptions) => {
  return useQuery({
    ...automationRuleQueries.detail(ruleId),
    // spread overrides replace `enabled`, so re-apply the ruleId presence guard.
    enabled: enabled && ruleId !== undefined,
    select: selectAutomationRuleDetailModel,
    ...queryCacheOptions,
    ...rest,
  })
}

interface GetAutomationRuleListResponse {
  rules: AutomationRuleListResponse[]
  pagination: PaginationResponse
}

export enum AutomationRuleListSortField {
  // use these with SortDirection
  Name = 'name',
  CreatedAt = 'createdAt',
}

interface GetAutomationRuleListFilters {
  search?: string
  status?: AutomationRuleStatusType[]
  sortField?: AutomationRuleListSortField
  sortDirection?: 'ASC' | 'DESC'
  fundingSources?: AutomationRuleFundingSource[]
  fundingSourceIds?: string[]
}

interface QueryAutomationRulesOption
  extends ExtendBaseInfiniteQueryOptions<
    GetAutomationRuleListResponse,
    number,
    AutomationRuleListModel[]
  > {
  filters: GetAutomationRuleListFilters
}

export const useAutomationRuleList = ({
  filters,
  ...rest
}: QueryAutomationRulesOption) => {
  return useExtendInfiniteQuery({
    queryKey: automationRulesQueryKeys.list(filters),
    queryUrl: ({ pageParam }) =>
      `/rules?${queryString.stringify({ page: pageParam, ...filters })}`,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => getNextExtendPageParam(lastPage.pagination),
    select: useCallback(
      (data: InfiniteData<GetAutomationRuleListResponse, number>) =>
        data.pages
          .flatMap((page) => page.rules)
          .map((rule) => new AutomationRuleListModel(rule)),
      [],
    ),
    ...rest,
  })
}
